import React from 'react';
import PropTypes from 'prop-types';

import { validateRosterProviderEmail } from '../../validation';
import { useFormContextSafe, useTriggerOnDisabledChange } from './hooks';

/**
 * @typedef {import('../../types').RosterProvider} RosterProvider
 */

import { FIELDS } from '../constants';
import FieldWrapper from './FieldWrapper';

/**
 * A self-contained email input field that:
 * - Registers itself with the parent form via `useFormContext`
 * - Validates email format and checks for duplicates
 * - Displays its own error state
 * - Validates on every keystroke (not just on blur)
 *
 * @param {Object} props
 * @param {boolean} props.disabled - Whether the field is disabled
 * @param {Array<RosterProvider>} [props.existingProviders=[]] - Providers already in roster for duplicate checking
 * @param {string} [props.providerEmail] - Email from selected provider (API); when value matches, professional check is skipped
 *
 * @example
 * // Inside a FormProvider:
 * <EmailField disabled={false} existingProviders={roster} providerEmail={foundProvider?.email} />
 */
export default function EmailField({
  disabled,
  existingProviders = [],
  providerEmail = '',
}) {
  const { register, formState, trigger } = useFormContextSafe();

  // Re-validate when disabled changes
  useTriggerOnDisabledChange(FIELDS.email, disabled);

  // Get error for this field
  const error = formState.errors?.[FIELDS.email];
  const errorMessage = typeof error?.message === 'string' ? error.message : '';

  // Register the input with validation rules
  // `register()` returns: { ref, name, onChange, onBlur }
  const { onChange: rhfOnChange, ...restRegistration } = register(
    FIELDS.email,
    {
      validate: (value = '') => {
        // Skip validation when disabled
        if (disabled) return true;

        const isAutopopulated =
          Boolean(providerEmail) && value?.trim() === providerEmail?.trim();
        const result = validateRosterProviderEmail(value, existingProviders, {
          skipProfessionalCheck: isAutopopulated,
        });

        return result.isValid
          ? true
          : result.error?.message || 'Validation failed';
      },
    },
  );

  /**
   * Handle input changes - update RHF state then trigger validation.
   * This gives us "validate on change" behavior even though the form uses mode: 'onBlur'.
   */
  const handleChange = (e) => {
    // First, let RHF update its internal state
    rhfOnChange(e);

    // Then trigger validation for this field
    trigger(FIELDS.email);
  };

  return (
    <FieldWrapper label="Provider's Email Address" errorMessage={errorMessage}>
      <input
        {...restRegistration}
        onChange={handleChange}
        type="email"
        placeholder={
          !disabled ? "Enter provider's email" : 'Select a provider first'
        }
        disabled={disabled}
        className="w-full"
        autoComplete="email"
        aria-invalid={!!error}
      />
    </FieldWrapper>
  );
}

EmailField.propTypes = {
  disabled: PropTypes.bool,
  existingProviders: PropTypes.arrayOf(PropTypes.object),
  providerEmail: PropTypes.string,
};
